// src/pages/Home.tsx
import { Link } from "react-router-dom";
import { ArrowRight, Zap, Shield, Globe } from "lucide-react";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import "../App.css";

const phrases = [
  "Global Payments",
  "Cross-Border Transactions",
  "Seamless Settlements",
  "Borderless Business Growth",
];

const features = [
  {
    icon: Zap,
    title: "Instant Payin",
    desc: "Easypaisa, JazzCash, Bank",
  },
  {
    icon: Shield,
    title: "Secure Payouts",
    desc: "USDT TRC20, manual reissue",
  },
  {
    icon: Globe,
    title: "D0 Settlement",
    desc: "Same-day crypto transfers",
  },
  {
    icon: Shield,
    title: "Real-time Dashboard",
    desc: "Track every transaction",
  },
];

const partners = [
  { name: "Easypaisa", src: "/partners/easypaisa.png" },
  { name: "JazzCash", src: "/partners/jazzcash.png" },
  { name: "Bank Transfer", src: "/partners/bank.png" },
  { name: "USDT", src: "/partners/usdt.svg" },
];

export default function Home() {
  const [index, setIndex] = useState(0);

  // Auto change carousel text every 3 seconds
  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % phrases.length);
    }, 3000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden py-24 mt-20 bg-gradient-to-br from-blue-50 via-white to-violet-50">
        {/* Animated gradient blobs */}
        <motion.div
          className="absolute -top-32 -left-32 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"
          animate={{ x: [0, 30, -20, 0], y: [0, 20, -30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-violet-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"
          animate={{ x: [0, -30, 40, 0], y: [0, -20, 30, 0] }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          {/* Title */}
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-snug">
            Your Local Gateway to
            <br />
            {/* Carousel animated text */}
            <motion.span
              key={index}
              className="inline-block bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.6 }}
            >
              {phrases[index]}
            </motion.span>
          </h1>

          <motion.p
            className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            Fast, secure, and seamless payments for Pakistani merchants. Accept
            local wallets, settle in USDT, and grow your business with zero
            friction.
          </motion.p>

          {/* Buttons with animation */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-500 to-violet-600 text-white font-semibold rounded-full hover:shadow-xl transition transform hover:scale-105"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>

            <Link
              to="/docs"
              className="inline-flex items-center px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-full hover:border-violet-600 hover:text-violet-600 transition transform hover:scale-105"
            >
              View API Docs
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Trusted Local Brand */}
      <section className="py-16 bg-white relative overflow-hidden">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/40 via-white to-violet-50/40 pointer-events-none"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="text-3xl font-bold mb-12 text-gray-900"
          >
            Built for Pakistan. Connected to the World.
          </motion.h2>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-8">
            {partners.map(({ name, src }, i) => (
              <motion.div
                key={name}
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{
                  y: -6,
                  scale: 1.05,
                  boxShadow:
                    "0 6px 15px rgba(99, 102, 241, 0.25), 0 0 6px rgba(59,130,246,0.15)",
                }}
                transition2={{
                  type: "spring",
                  stiffness: 300,
                  damping: 20,
                  duration: 0.2,
                }}
                className="bg-gray-50 rounded-2xl p-6 shadow-sm cursor-pointer flex flex-col items-center justify-center transition-all duration-150"
              >
                <motion.div
                  animate={{ scale: [1, 1.03, 1] }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.2,
                  }}
                  className="w-24 h-16 flex items-center justify-center mb-4"
                >
                  <img
                    src={src}
                    alt={name}
                    className="max-h-16 max-w-full object-contain mix-blend-multiply"
                  />
                </motion.div>
                <p className="font-medium text-gray-700">{name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div class="container">
          <h1 className="text-3xl font-bold text-center mb-12">How It Works</h1>

          <div class="steps-wrapper">
            <div class="steps-container">
              <div class="progress-line">
                <div class="progress-line-fill"></div>
              </div>

              <div class="step">
                <div class="step-circle">1</div>
                <div class="step-content">
                  <div class="step-title">Sign Up</div>
                  <div class="step-description">
                    Create your merchant account in minutes
                  </div>
                </div>
              </div>

              <div class="step">
                <div class="step-circle">2</div>
                <div class="step-content">
                  <div class="step-title">Integrate</div>
                  <div class="step-description">
                    Add our API or use redirect flows
                  </div>
                </div>
              </div>

              <div class="step">
                <div class="step-circle">3</div>
                <div class="step-content">
                  <div class="step-title">Get Paid</div>
                  <div class="step-description">
                    Receive payments, settle in USDT
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Everything You Need
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30, scale: 0.95 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.6, delay: i * 0.2, ease: "easeOut" }}
                whileHover={{
                  y: -8,
                  boxShadow:
                    "0 10px 25px rgba(139, 92, 246, 0.3), 0 0 10px rgba(59,130,246,0.2)",
                }}
                className="relative bg-gradient-to-br from-blue-50 to-violet-50 rounded-2xl p-6 transition-transform duration-300"
              >
                <motion.div
                  animate={{
                    y: [0, -6, 0],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: i * 0.5,
                  }}
                >
                  <f.icon className="w-10 h-10 text-violet-600 mb-4 mx-auto" />
                </motion.div>

                <h3 className="text-lg font-semibold mb-2 text-center">
                  {f.title}
                </h3>
                <p className="text-sm text-gray-600 text-center">{f.desc}</p>

                {/* gradient border glow */}
                <div className="absolute inset-0 rounded-2xl border border-transparent hover:border-violet-300 transition-all duration-500"></div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Settlement Highlight */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-violet-600 text-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-3xl font-bold mb-4">
            Transparent Settlements at Binance Official Rate
          </h2>
          <p className="text-xl mb-8">
            Every conversion uses Binance's real-time rate. No hidden spreads.
            Full audit trail.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-3 bg-white text-violet-600 font-medium rounded-full hover:bg-gray-100 transition"
          >
            Get your quote
            {/* <ArrowRight className="ml-2 w-5 h-5" /> */}
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-4xl font-bold mb-4">
            Start Accepting Payments Today
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join hundreds of Pakistani merchants already growing with ZINDI.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-10 py-5 bg-gradient-to-r from-blue-500 to-violet-600 text-white text-lg font-medium rounded-full hover:shadow-2xl transition"
          >
            Get Started Free
            <ArrowRight className="ml-3 w-6 h-6" />
          </Link>
        </div>
      </section>
    </div>
  );
}
